import React from 'react';
import { Quote, ThumbsUp, HardHat, Home } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import StarRating from '@/components/testimonials/StarRating';
import { Testimonial, displayName, displayLocation } from '@/lib/testimonials';

/**
 * Public review card. Renders exactly what the spec asks for and nothing more:
 * rating, the review text, "– John M., Riverside, CA" and the customer type.
 * The email address is never projected into this component's data.
 */
const TestimonialCard: React.FC<{ item: Testimonial; className?: string }> = ({
  item,
  className = '',
}) => {
  const { t } = useI18n();
  const isContractor = item.customer_type === 'contractor';
  const TypeIcon = isContractor ? HardHat : Home;

  return (
    <figure
      className={`flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-card transition-all duration-200 hover:-translate-y-1 hover:shadow-card-hover ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <StarRating value={item.rating} size="sm" />
        <Quote className="h-6 w-6 shrink-0 text-slate-200" aria-hidden="true" />
      </div>

      <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-slate-700">
        “{item.body}”
      </blockquote>

      <figcaption className="mt-5 border-t border-slate-100 pt-4">
        <p className="text-sm font-bold text-slate-900">
          – {displayName(item.name)}, {displayLocation(item.city, item.state)}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
              isContractor
                ? 'bg-slate-100 text-slate-700 ring-slate-200'
                : 'bg-orange-50 text-orange-700 ring-orange-200'
            }`}
          >
            <TypeIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {isContractor ? t('testi.typeContractor') : t('testi.typeHomeowner')}
          </span>
          {item.recommend && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200">
              <ThumbsUp className="h-3.5 w-3.5" aria-hidden="true" />
              {t('testi.recommends')}
            </span>
          )}
        </div>
      </figcaption>
    </figure>
  );
};

export default TestimonialCard;
